class AltoFiltersDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-overlay]');
    this.panel = this.querySelector('[data-panel]');
    this.form = this.panel;
    this.closeButton = this.querySelector('[data-close]');
    this.resetButton = this.querySelector('[data-alto-filters-reset]');
    this.applyButton = this.querySelector('[data-alto-filters-apply]');
    this.activeElement = null;
    this.triggerHandlers = new WeakMap();

    this.handleOverlayClick = this.close.bind(this);
    this.handleCloseClick = this.close.bind(this);
    this.handleResetClick = this.reset.bind(this);
    this.handleFormChange = (event) => {
      this.updateApplyState();
      if (event.target?.name === 'sort_by') {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData).toString();
        this.close();
        this.renderPage(searchParams, true);
      }
    };
    this.handleFormSubmit = this.onSubmit.bind(this);
    this.handlePopState = this.onPopState.bind(this);
    this.handleKeyup = (event) => {
      if (event.code === 'Escape') this.close();
    };

    this.overlay?.addEventListener('click', this.handleOverlayClick);
    this.closeButton?.addEventListener('click', this.handleCloseClick);
    this.resetButton?.addEventListener('click', this.handleResetClick);
    this.form?.addEventListener('change', this.handleFormChange);
    this.form?.addEventListener('input', this.handleFormChange);
    this.form?.addEventListener('submit', this.handleFormSubmit);
    this.addEventListener('keyup', this.handleKeyup);
    window.addEventListener('popstate', this.handlePopState);

    this.bindTriggers();
    this.updateApplyState();
  }

  disconnectedCallback() {
    this.overlay?.removeEventListener('click', this.handleOverlayClick);
    this.closeButton?.removeEventListener('click', this.handleCloseClick);
    this.resetButton?.removeEventListener('click', this.handleResetClick);
    this.form?.removeEventListener('change', this.handleFormChange);
    this.form?.removeEventListener('input', this.handleFormChange);
    this.form?.removeEventListener('submit', this.handleFormSubmit);
    this.removeEventListener('keyup', this.handleKeyup);
    window.removeEventListener('popstate', this.handlePopState);
    this.unbindTriggers();
  }

  updateApplyState() {
    if (!this.applyButton || !this.form) return;
    const SKIP_NAMES = new Set(['sort_by', 'q', 'options[prefix]']);
    let count = 0;
    for (const input of this.form.elements) {
      if (!input.name || SKIP_NAMES.has(input.name)) continue;
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (input.checked) count += 1;
      } else if (input.value && input.value.trim() !== '') {
        count += 1;
      }
    }
    this.applyButton.disabled = count === 0;
  }

  bindTriggers() {
    const selector = `[data-alto-filters-trigger][aria-controls="${this.id}"]`;
    document.querySelectorAll(selector).forEach((trigger) => {
      if (this.triggerHandlers.has(trigger)) return;
      trigger.setAttribute('aria-haspopup', 'dialog');
      const handler = (event) => {
        event.preventDefault();
        this.open(trigger);
      };
      trigger.addEventListener('click', handler);
      this.triggerHandlers.set(trigger, handler);
    });
  }

  unbindTriggers() {
    const selector = `[data-alto-filters-trigger][aria-controls="${this.id}"]`;
    document.querySelectorAll(selector).forEach((trigger) => {
      const handler = this.triggerHandlers.get(trigger);
      if (handler) trigger.removeEventListener('click', handler);
    });
  }

  open(triggeredBy) {
    if (this.dataset.state === 'open') return;
    if (triggeredBy) this.activeElement = triggeredBy;
    this.dataset.state = 'open';
    this.panel?.removeAttribute('inert');
    this.panel?.removeAttribute('aria-hidden');
    document.body.classList.add('overflow-hidden');
    this.activeElement?.setAttribute('aria-expanded', 'true');

    this.addEventListener(
      'transitionend',
      () => {
        if (typeof trapFocus === 'function') {
          trapFocus(this.panel, this.closeButton || this.panel);
        } else {
          this.panel?.focus();
        }
      },
      { once: true }
    );
  }

  close() {
    if (this.dataset.state === 'closed') return;
    this.dataset.state = 'closed';
    this.panel?.setAttribute('inert', '');
    this.panel?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    this.activeElement?.setAttribute('aria-expanded', 'false');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    } else {
      this.activeElement?.focus();
    }
    this.activeElement = null;
  }

  reset() {
    const url = this.dataset.resetUrl || window.location.pathname;
    window.location.href = url;
  }

  onSubmit(event) {
    event.preventDefault();
    const formData = new FormData(this.form);
    const searchParams = new URLSearchParams(formData).toString();
    this.close();
    this.renderPage(searchParams, true);
  }

  onPopState(event) {
    const searchParams = event.state?.searchParams ?? new URLSearchParams(window.location.search).toString();
    this.renderPage(searchParams, false);
  }

  getSectionId() {
    // Cache the section id: the empty-results state renders no #product-grid
    // element, so without this a follow-up action (e.g. switching tabs back
    // after a zero-result filter) would have no id to re-render with.
    const fromGrid = document.getElementById('product-grid')?.dataset.id;
    if (fromGrid) AltoFiltersDrawer.sectionId = fromGrid;
    return AltoFiltersDrawer.sectionId;
  }

  renderPage(searchParams, updateHistory) {
    const sectionId = this.getSectionId();
    if (!sectionId) {
      // No section id to re-render with (e.g. landing directly on a zero-result
      // filtered URL, where #product-grid is never rendered). Fall back to a
      // full navigation so the caller — a tab click that already called
      // preventDefault() — isn't left as a dead click.
      window.location.search = searchParams;
      return;
    }

    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) gridContainer.classList.add('alto-loading');

    const fetchUrl = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;

    fetch(fetchUrl)
      .then((r) => r.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        const newGrid = parsed.getElementById('ProductGridContainer');
        if (newGrid && gridContainer) {
          gridContainer.innerHTML = newGrid.innerHTML;
          gridContainer.classList.remove('alto-loading');
        }

        // Replace the drawer element so Liquid-rendered active filter state is fresh
        const newDrawer = parsed.querySelector('alto-filters-drawer');
        if (newDrawer && this.parentNode) {
          this.parentNode.replaceChild(newDrawer, this);
        }

        // Replace the category tab strip so its active tab tracks the applied
        // filters (it lives outside #ProductGridContainer, so it isn't covered
        // by the grid swap above).
        const currentTabs = document.getElementById('AltoCollectionTabs');
        const newTabs = parsed.getElementById('AltoCollectionTabs');
        if (currentTabs && newTabs && currentTabs.parentNode) {
          currentTabs.parentNode.replaceChild(newTabs, currentTabs);
        }

        if (updateHistory) {
          history.pushState(
            { searchParams },
            '',
            `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`
          );
        }
      })
      .catch(() => {
        // On failure fall back to a normal navigation so the user isn't stuck
        if (updateHistory) {
          window.location.search = searchParams;
        }
        gridContainer?.classList.remove('alto-loading');
      });
  }
}

if (!customElements.get('alto-filters-drawer')) {
  customElements.define('alto-filters-drawer', AltoFiltersDrawer);
}
